import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {
  applyPendingIssueSuggestionsMutation,
  applyPendingIssueSuggestionsMutation$data,
} from './__generated__/applyPendingIssueSuggestionsMutation.graphql'

// Must match the args used by the `recentTriageEvents` field in
// `useRecentAgentActivity_issue` so we can look up the same store record.
const RECENT_TRIAGE_ITEM_TYPES = [
  'LABELED_EVENT',
  'UNLABELED_EVENT',
  'ISSUE_TYPE_ADDED_EVENT',
  'ISSUE_TYPE_CHANGED_EVENT',
  'ISSUE_TYPE_REMOVED_EVENT',
  'ISSUE_FIELD_CHANGED_EVENT',
  'ISSUE_FIELD_ADDED_EVENT',
  'ISSUE_FIELD_REMOVED_EVENT',
]

type BaseOptimisticEvent = {
  actorTypename: string
  actorLogin: string
  actorAvatarUrl: string
  actorDisplayName: string | null
  rationale: string | null
}

export type ApplySuggestionRef =
  | {
      kind: 'TYPE'
      issueTypeId: string
      optimisticEvent: BaseOptimisticEvent & {
        issueTypeName: string
        issueTypeColor: string | null
      }
    }
  | {
      kind: 'LABEL'
      labelId: string
      optimisticEvent: BaseOptimisticEvent & {
        labelName: string
        labelColor: string | null
      }
    }
  | {
      kind: 'FIELD'
      issueFieldId: string
      optimisticEvent: BaseOptimisticEvent & {
        fieldName: string
        value: string | null
        color: string | null
      }
    }

export function commitApplyPendingIssueSuggestionsMutation({
  environment,
  issueId,
  actorId,
  suggestions,
  since,
  onError,
  onCompleted,
}: {
  environment: Environment
  issueId: string
  actorId: string
  suggestions: ApplySuggestionRef[]
  since: string
  onError?: (error: Error) => void
  onCompleted?: (response: applyPendingIssueSuggestionsMutation$data) => void
}) {
  return commitMutation<applyPendingIssueSuggestionsMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation applyPendingIssueSuggestionsMutation($input: ApplyPendingIssueSuggestionsInput!) {
        applyPendingIssueSuggestions(input: $input) {
          clientMutationId
        }
      }
    `,
    variables: {
      input: {
        issueId,
        actorId,
        suggestions: suggestions.map(s => {
          if (s.kind === 'TYPE') return {kind: 'TYPE', issueTypeId: s.issueTypeId}
          if (s.kind === 'LABEL') return {kind: 'LABEL', labelId: s.labelId}
          return {kind: 'FIELD', issueFieldId: s.issueFieldId}
        }),
      },
    },
    optimisticUpdater: store => applyOptimisticUpdate(store, issueId, actorId, suggestions, since),
    updater: store => applyOptimisticUpdate(store, issueId, actorId, suggestions, since),
    onError: error => onError?.(error),
    onCompleted: response => onCompleted?.(response),
  })
}

function applyOptimisticUpdate(
  store: RecordSourceSelectorProxy,
  issueId: string,
  actorId: string,
  suggestions: ApplySuggestionRef[],
  since: string,
) {
  const issue = store.get(issueId)
  if (!issue) return

  const timelineArgs = {
    last: 25,
    since,
    visibleEventsOnly: true,
    itemTypes: RECENT_TRIAGE_ITEM_TYPES,
  }
  const timeline = issue.getLinkedRecord('timelineItems', timelineArgs)
  let timelineNodes = timeline?.getLinkedRecords('nodes') ?? []
  const pendingSuggestions = issue.getLinkedRecords('pendingSuggestions') ?? []

  for (const suggestion of suggestions) {
    const {eventRecord, suggestionTypename, suggestionMatch} = buildOptimisticEvent(store, actorId, suggestion)

    // 1. Mark the matching pending suggestion as APPLIED so the row hides.
    for (const s of pendingSuggestions) {
      if (!s) continue
      if (s.getValue('__typename') !== suggestionTypename) continue
      if (suggestionMatch(s)) {
        // Relay stores fields under the schema name (`state`), not the fragment alias.
        s.setValue('APPLIED', 'state')
      }
    }

    // 2. Append the fake event to the timeline.
    timelineNodes = [...timelineNodes, eventRecord]

    // 3. Attach an IssueUpdateIntent to the event so the action row gets a rationale.
    const intentRecordId = `client:optimistic-intent:${eventRecord.getDataID()}`
    const intentRecord = store.create(intentRecordId, 'IssueUpdateIntent')
    intentRecord.setValue(suggestion.optimisticEvent.rationale ?? '', 'rationale')
    eventRecord.setLinkedRecord(intentRecord, 'intent')
  }

  if (timeline) {
    timeline.setLinkedRecords(timelineNodes, 'nodes')
  }
}

function buildOptimisticEvent(store: RecordSourceSelectorProxy, actorId: string, suggestion: ApplySuggestionRef) {
  const event = suggestion.optimisticEvent
  const now = new Date().toISOString()
  const subjectId =
    suggestion.kind === 'TYPE'
      ? suggestion.issueTypeId
      : suggestion.kind === 'LABEL'
        ? suggestion.labelId
        : suggestion.issueFieldId
  const uniqueSuffix = `${actorId}:${subjectId}:${Date.now()}`

  const actorRecordId = `client:optimistic-actor:${uniqueSuffix}`
  const actorRecord = store.create(actorRecordId, event.actorTypename)
  actorRecord.setValue(event.actorTypename, '__typename')
  actorRecord.setValue(event.actorLogin, 'login')
  actorRecord.setValue(event.actorAvatarUrl, 'avatarUrl')
  if (event.actorDisplayName) {
    actorRecord.setValue(event.actorDisplayName, 'displayName')
  }

  const eventId = `client:optimistic-event:${uniqueSuffix}`

  if (suggestion.kind === 'TYPE') {
    const issueTypeRecordId = `client:optimistic-issueType:${suggestion.issueTypeId}:${Date.now()}`
    const issueTypeRecord = store.create(issueTypeRecordId, 'IssueType')
    issueTypeRecord.setValue(suggestion.optimisticEvent.issueTypeName, 'name')
    if (suggestion.optimisticEvent.issueTypeColor) {
      issueTypeRecord.setValue(suggestion.optimisticEvent.issueTypeColor, 'color')
    }
    const eventRecord = store.create(eventId, 'IssueTypeAddedEvent')
    eventRecord.setValue('IssueTypeAddedEvent', '__typename')
    eventRecord.setValue(eventId, 'id')
    eventRecord.setValue(now, 'createdAt')
    eventRecord.setLinkedRecord(issueTypeRecord, 'issueType')
    eventRecord.setLinkedRecord(actorRecord, 'actor')
    return {
      eventRecord,
      suggestionTypename: 'PendingTypeSuggestion',
      suggestionMatch: (s: ReturnType<RecordSourceSelectorProxy['get']>) => {
        const actor = s?.getLinkedRecord('actor')
        const issueType = s?.getLinkedRecord('issueType')
        return actor?.getValue('id') === actorId && issueType?.getValue('id') === suggestion.issueTypeId
      },
    }
  }

  if (suggestion.kind === 'LABEL') {
    const labelRecordId = `client:optimistic-label:${suggestion.labelId}:${Date.now()}`
    const labelRecord = store.create(labelRecordId, 'Label')
    labelRecord.setValue(suggestion.optimisticEvent.labelName, 'name')
    if (suggestion.optimisticEvent.labelColor) {
      labelRecord.setValue(suggestion.optimisticEvent.labelColor, 'color')
    }
    const eventRecord = store.create(eventId, 'LabeledEvent')
    eventRecord.setValue('LabeledEvent', '__typename')
    eventRecord.setValue(eventId, 'id')
    eventRecord.setValue(now, 'createdAt')
    eventRecord.setLinkedRecord(labelRecord, 'label')
    eventRecord.setLinkedRecord(actorRecord, 'actor')
    return {
      eventRecord,
      suggestionTypename: 'PendingLabelSuggestion',
      suggestionMatch: (s: ReturnType<RecordSourceSelectorProxy['get']>) => {
        const actor = s?.getLinkedRecord('actor')
        const label = s?.getLinkedRecord('label')
        return actor?.getValue('id') === actorId && label?.getValue('id') === suggestion.labelId
      },
    }
  }

  // FIELD
  const fieldRecordId = `client:optimistic-field:${suggestion.issueFieldId}:${Date.now()}`
  // We use IssueFieldText as a stand-in concrete type for IssueFieldCommon.
  const fieldRecord = store.create(fieldRecordId, 'IssueFieldText')
  fieldRecord.setValue(suggestion.optimisticEvent.fieldName, 'name')
  const eventRecord = store.create(eventId, 'IssueFieldAddedEvent')
  eventRecord.setValue('IssueFieldAddedEvent', '__typename')
  eventRecord.setValue(eventId, 'id')
  eventRecord.setValue(now, 'createdAt')
  if (suggestion.optimisticEvent.value) {
    eventRecord.setValue(suggestion.optimisticEvent.value, 'value')
  }
  if (suggestion.optimisticEvent.color) {
    eventRecord.setValue(suggestion.optimisticEvent.color, 'color')
  }
  eventRecord.setLinkedRecord(fieldRecord, 'issueField')
  eventRecord.setLinkedRecord(actorRecord, 'actor')
  return {
    eventRecord,
    suggestionTypename: 'PendingFieldSuggestion',
    suggestionMatch: (s: ReturnType<RecordSourceSelectorProxy['get']>) => {
      const actor = s?.getLinkedRecord('actor')
      const issueField = s?.getLinkedRecord('issueField')
      return actor?.getValue('id') === actorId && issueField?.getValue('id') === suggestion.issueFieldId
    },
  }
}
